import { sva } from '@allenai/varnish-panda-runtime/css';
import type { RecipeVariantProps } from '@allenai/varnish-panda-runtime/types';

/**
 *  Radio can be built with the <input> HTML element, but this can be difficult to style.
 *   Radio helps achieve accessible radio inputs that can be styled as needed.
 *
 *  See: https://react-spectrum.adobe.com/react-aria/RadioGroup.html
 */

/**
 * @remarks A radio recipe
 *
 * @param size - Size of the radio, values are `small`, `medium` (default), and `large`
 * @param color - Color of the radio, values are:
 *   `primary` (default), `secondary`, `tertiary`, `teal`, and `cream`
 */

const radioRecipe = sva({
    slots: ['root', 'circle'],
    base: {
        root: {
            display: 'flex',
            gap: '[8px]',
            alignItems: 'center',
            cursor: 'pointer',
            color: 'text',

            colorPalette: 'elements.overrides.form.radio',

            _disabled: {
                cursor: 'not-allowed',
                colorPalette: 'elements.overrides.form.radio.disabled',
            },
            _invalid: {
                colorPalette: 'elements.overrides.form.radio.error',
            },
            _componentReadOnly: {
                cursor: 'default',
            },
        },
        circle: {
            boxSizing: 'border-box',
            position: 'relative',
            width: '[20px]',
            height: '[20px]',
            flexShrink: '0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 'full',
            border: '[1px solid]',
            borderColor: 'colorPalette.stroke',

            // All unselected radios have the default fill
            backgroundColor: 'elements.overrides.form.radio.fill',
            _before: {
                content: '""',
                display: 'block',
                aspectRatio: '1',
                borderRadius: 'full',
            },
            _groupHoverNotDisabledNotSelectedNotInvalid: {
                backgroundColor: 'colorPalette.hovered.fill',
                borderColor: 'colorPalette.hovered.stroke',
            },
            _groupSelected: {
                borderColor: 'transparent',
                backgroundColor: 'colorPalette.fill',
                _before: {
                    backgroundColor: 'colorPalette.contrast',
                },
            },
        },
    },
    variants: {
        size: {
            small: {
                root: {
                    fontSize: '[12px]',
                },
                circle: {
                    width: '[0.75rem]',
                    height: '[0.75rem]',
                    _before: {
                        width: '[0.25rem]',
                    },
                },
            },
            medium: {
                root: {
                    fontSize: 'sm',
                },
                circle: {
                    width: '[0.875rem]',
                    height: '[0.875rem]',
                    _before: {
                        width: '[0.375rem]',
                    },
                },
            },
            large: {
                root: {
                    fontSize: 'md',
                },
                circle: {
                    width: '[1.25rem]',
                    height: '[1.25rem]',
                    _before: {
                        width: '[0.5rem]',
                    },
                },
            },
        },
        color: {
            default: {
                root: {
                    _selectedNotDisabledNotInvalid: {
                        colorPalette: 'elements.overrides.form.radio.checked',
                    },
                },
            },
            // reversed: {},
            primary: {
                root: {
                    _selectedNotDisabledNotInvalid: {
                        colorPalette: 'elements.primary',
                    },
                },
            },
            secondary: {
                root: {
                    _selectedNotDisabledNotInvalid: {
                        colorPalette: 'elements.secondary',
                    },
                },
            },
            tertiary: {
                root: {
                    _selectedNotDisabledNotInvalid: {
                        colorPalette: 'elements.tertiary',
                    },
                },
            },
        },
    },
    defaultVariants: {
        size: 'medium',
        color: 'default',
    },
});

type RadioRecipeProps = RecipeVariantProps<typeof radioRecipe>;

export { radioRecipe, radioRecipe as default };
export type { RadioRecipeProps };
